import type { Locale } from "./types";

export type Dict = {
  nav: {
    home: string;
    ia: string;
    integrations: string;
    cases: string;
    pricing: string;
    blog: string;
    about: string;
    contact: string;
    demoCta: string;
    whatsapp: string;
    talkWhatsapp: string;
    openMenu: string;
    closeMenu: string;
    backHome: string;
    features: string;
  };
  footer: {
    tagline: string;
    productCol: string;
    companyCol: string;
    legalCol: string;
    rights: string;
    address: string;
    privacy: string;
    privacyNotice: string;
    ombudsman: string;
  };
  whatsappFab: { label: string };
  mobileSticky: { demo: string; whatsapp: string };
  forms: {
    name: string;
    namePh: string;
    phone: string;
    phonePh: string;
    company: string;
    companyPh: string;
    email: string;
    emailPh: string;
    emailOptional: string;
    segment: string;
    segmentPh: string;
    message: string;
    messagePh: string;
    submit: string;
    submitting: string;
    submitDemo: string;
    requestDemo: string;
    success: string;
    successDesc: string;
    error: string;
    errorDesc: string;
    sentTitle: string;
    sentBody: string;
    sendAnother: string;
    requiredMark: string;
    vNameMin: string;
    vPhone: string;
    vPhoneInvalid: string;
    vCompany: string;
    vSegment: string;
    vEmail: string;
    waDefault: string;
    waContact: (company: string, segment: string) => string;
  };
  contactBlock: {
    eyebrow: string;
    titlePrefix: string;
    titleHighlight: string;
    titleSuffix: string;
    description: string;
    address: string;
    attendance: string;
    attendanceValue: string;
  };
  contactPage: {
    titlePrefix: string;
    titleHighlight: string;
    titleSuffix: string;
    description: string;
    eyebrow: string;
    metaTitle: string;
    metaDesc: string;
  };
  finalCta: {
    titlePrefix: string;
    titleHighlight: string;
    titleSuffix: string;
    description: string;
    talkNow: string;
  };
  home: { metaTitle: string; metaDesc: string };
  common: { learnMore: string; scheduleDemo: string; seeMore: string; languageLabel: string };

  // ====== Home sections ======
  hero: {
    badge: string;
    titleA: string;
    titleHighlight: string;
    descriptionHtml: string; // contains <strong> tags
    ctaPrimary: string;
    ctaSecondary: string;
    social1: string;
    social2: string;
    social3: string;
    mockupTitle: string;
    slaRisk: string;
    aiSuggested: string;
    metricConversions: string;
    metricQueue: string;
    metricAi: string;
  };
  pillars: {
    badge: string;
    titleA: string;
    titleHighlight: string;
    description: string;
    chat: { title: string; desc: string };
    crm: { title: string; desc: string };
    ai: { title: string; desc: string };
    ctaPrimary: string;
    ctaSecondary: string;
  };
  credibility: { aria: string; items: string[] };
  problem: {
    titleA: string;
    titleHighlight: string;
    pains: string[];
    bottomLine: string;
    bottomLineStrong: string;
  };
  product: {
    titleA: string;
    titleHighlight: string;
    items: { title: string; text: string }[];
  };
  whatsappApi: {
    badge: string;
    titleA: string;
    titleHighlight: string;
    description: string;
    items: { title: string; text: string }[];
    cta: string;
  };
  segments: {
    titleA: string;
    titleHighlight: string;
    description: string;
    viewCase: string;
    test: string;
    bridge: {
      badge: string;
      titleA: string;
      titleHighlight: string;
      description: string;
      cta: string;
    };
  };
  realResults: {
    titleA: string;
    titleHighlight: string;
    description: string;
    statLabels: string[];
    statNotes: string[];
    recognitionEyebrow: string;
    recognitionText: string;
  };
  lossCalc: {
    badge: string;
    titleA: string;
    titleHighlight: string;
    description: string;
    f1: string;
    f2: string;
    f3: string;
    suffixConvs: string;
    lossLabel: string;
    lossFormula: string;
    recoveryEyebrow: string;
    recoveryPrefix: string;
    recoveryHint: string;
    cta: string;
    disclaimer: string;
    calcMsgLabels: { calculator: string; convsPerDay: string; ticket: string; pctNoReply: string; monthlyLoss: string };
  };
  casesSection: {
    titleA: string;
    titleHighlight: string;
    description: string;
    featuredBadge: string;
    closeAria: string;
    iframeTitle: string;
    playlistCta: string;
    items: { title: string; client: string; segment: string }[];
  };
  faq: {
    titleA: string;
    titleHighlight: string;
    items: { q: string; a: string }[];
  };
};

export const HERO_DESC: Record<Locale, string> = {
  pt: "A <strong>Kentro</strong> une <strong>Chat</strong> omnichannel, <strong>CRM</strong> e <strong>IA</strong> em uma única plataforma: WhatsApp, Instagram, voz, e-mail e webchat com agentes que entendem contexto e resolvem do primeiro contato ao pós-venda.",
  en: "<strong>Kentro</strong> brings together omnichannel <strong>Chat</strong>, <strong>CRM</strong> and <strong>AI</strong> in a single platform: WhatsApp, Instagram, voice, email and web chat with agents that understand context and solve issues from first contact to post-sale.",
  es: "<strong>Kentro</strong> une <strong>Chat</strong> omnicanal, <strong>CRM</strong> e <strong>IA</strong> en una sola plataforma: WhatsApp, Instagram, voz, correo y webchat con agentes que entienden el contexto y resuelven desde el primer contacto hasta la posventa.",
};

export const CASE_CLIENTS = [
  { client: "Farmácia Bom Preço · Wesley Danilo" },
  { client: "Dental Center · Dr. Felipe Mafra" },
  { client: "Dr. Rafael Antunes" },
  { client: "Clínica Viva & Sorria · Dra. Greise Antunes" },
  { client: "Santa Clara" },
  { client: "Katira Pet" },
  { client: "Katira Pet" },
];

